import type { ApiError } from "./client"
import useCustomToast from "./hooks/useCustomToast"

export const emailPattern = {
  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  message: "Invalid email address",
}

export const namePattern = {
  value: /^[A-Za-z\s\u00C0-\u017F]{1,30}$/,
  message: "Invalid name",
}

export const passwordRules = (isRequired = true) => {
  const rules: any = {
    minLength: {
      value: 8,
      message: "Password must be at least 8 characters",
    },
  }

  if (isRequired) {
    rules.required = "Password is required"
  }

  return rules
}

export const confirmPasswordRules = (
  getValues: () => any,
  isRequired = true,
) => {
  const rules: any = {
    validate: (value: string) => {
      const password = getValues().password || getValues().new_password
      return value === password ? true : "The passwords do not match"
    },
  }

  if (isRequired) {
    rules.required = "Password confirmation is required"
  }

  return rules
}

export const handleError = (err: ApiError) => {
  const { showErrorToast } = useCustomToast()
  const errDetail = (err.body as any)?.detail
  let errorMessage = errDetail || "Something went wrong."
  if (Array.isArray(errDetail) && errDetail.length > 0) {
    errorMessage = errDetail[0].msg
  }
  showErrorToast(errorMessage)
}

/**
 * Decode JWT token payload without verification (client-side only).
 * Returns null if token is invalid or missing.
 */
export const decodeJWT = (token: string): Record<string, any> | null => {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null

    // Decode the payload (second part)
    const payload = parts[1]
    // Replace URL-safe characters and add padding if needed
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/")
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    )

    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    )

    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error("Failed to decode JWT:", error)
    return null
  }
}

/**
 * Check if the current session is an embedded application session.
 * Returns true if the access token has the is_embed claim set to true.
 */
export const isEmbeddedSession = (): boolean => {
  const token = localStorage.getItem("access_token")
  if (!token) return false

  const payload = decodeJWT(token)
  return payload?.is_embed === true
}

/**
 * Extract the active organization ID from the stored access token.
 * Returns null if no token is found or the claim is missing.
 */
export const getCurrentOrgId = (): string | null => {
  const token = localStorage.getItem("access_token")
  if (!token) return null

  const payload = decodeJWT(token)
  const orgId = payload?.org_id

  return typeof orgId === "string" ? orgId : null
}

/**
 * Extract the signed-in user's ID (JWT `sub`) from the stored access token
 * without a network request. Works for embedded (iframe) sessions too.
 */
export const getCurrentUserId = (): string | null => {
  const token =
    localStorage.getItem("access_token") ??
    sessionStorage.getItem("access_token")
  if (!token) return null

  const subject = decodeJWT(token)?.sub
  return typeof subject === "string" ? subject : null
}
